import React, { useRef, useEffect } from "react"
import { Text, Box, Flex, Heading, Container } from "theme-ui"
import { MagicLink } from "../utils/magicLink"
import { useBreakpointIndex } from "@theme-ui/match-media"
import Texture from "../assets/images/texture.svg"
import { ArrowRightCircle } from "react-feather"
import { OutboundLink } from "./link"
import { useHelloBar } from "../hooks/useHelloBar"
import { getArticlePath } from "../utils/path"
import { InboundLink } from "./link"
import { Link } from "gatsby"
import { useBanner } from "../context/BannerContext"

const BannerTop = () => {
  const breakpointIndex = useBreakpointIndex()
  const helloBar = useHelloBar()
  const { isBannerVisible, setBannerHeight, isMobile } = useBanner()
  const bannerRef = useRef(null)

  // Misura l'altezza del banner e passa al contesto
  useEffect(() => {
    if (bannerRef.current) {
      setBannerHeight(bannerRef.current.offsetHeight)

      // Ricontrollo l'altezza quando il DOM è completamente caricato
      const checkHeight = () => {
        if (bannerRef.current) {
          setBannerHeight(bannerRef.current.offsetHeight)
        }
      }

      // Check immediato e dopo un breve ritardo per sicurezza
      checkHeight()
      const timer = setTimeout(checkHeight, 100)
      return () => clearTimeout(timer)
    }
  }, [helloBar.animate, setBannerHeight])

  // Non mostrare il banner se non c'è il contenuto o animate è false
  if (!helloBar || !helloBar.animate) {
    return null
  }

  return (
    <Box
      ref={bannerRef}
      sx={{
        position: isMobile ? "fixed" : "relative",
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        zIndex: 999,
        backgroundColor: "primary",
        transform:
          isMobile && !isBannerVisible ? "translateY(-100%)" : "translateY(0)",
        transition: "transform 300ms cubic-bezier(0.4, 0.0, 0.2, 1)",
        willChange: "transform",
        py: [2, 2, 2, 2],
        a: {
          textDecoration: "none",
        },
        overflow: "hidden",
        whiteSpace: "nowrap",
        borderBottom: "none",
      }}
    >
      <Flex
        sx={{
          "@keyframes marquee": {
            from: { transform: "translateX(0)" },
            to: { transform: "translateX(-50%)" },
          },
          animation: "marquee 60s linear infinite",
          width: "max-content",
          display: "flex",
          "& > a": {
            flex: "0 0 auto",
          },
        }}
      >
        {[...Array(5)].map((_, i) =>
          helloBar.news.model.apiKey === "article" ? (
            <InboundLink
              key={`original-${i}`}
              to={getArticlePath(helloBar.news, helloBar.news.locale)}
              sx={{
                display: "inline-flex",
                textDecoration: "none",
              }}
            >
              <Flex
                sx={{
                  alignItems: "center",
                  gap: 5,
                  mx: 5,
                  px: 4,
                  color: "light",
                }}
              >
                <Heading
                  as="h3"
                  variant="h4"
                  sx={{
                    my: [0],
                    fontSize: [1, 5],
                    color: "light",
                    whiteSpace: "nowrap",
                    fontWeight: "initial",
                  }}
                >
                  {helloBar.title}
                </Heading>
                <Flex
                  sx={{
                    alignItems: "center",
                    gap: 3,
                    bg: "light",
                    color: "primary",
                    px: 3,
                    py: 2,
                    whiteSpace: "nowrap",
                    borderRadius: "0",
                  }}
                >
                  <Text sx={{ fontSize: [0, 1, 1, 2] }}>Leggi la news</Text>
                  <ArrowRightCircle size={20} />
                </Flex>
              </Flex>
            </InboundLink>
          ) : (
            <Link
              key={`original-${i}`}
              to={helloBar.news.url}
              target="_blank"
              sx={{
                display: "inline-flex",
                textDecoration: "none",
              }}
            >
              <Flex
                sx={{
                  alignItems: "center",
                  gap: 5,
                  mx: 5,
                  px: 4,
                  color: "light",
                }}
              >
                <Heading
                  as="h3"
                  variant="h4"
                  sx={{
                    my: [0],
                    fontSize: [1, 5],
                    color: "light",
                    whiteSpace: "nowrap",
                    fontWeight: "initial",
                  }}
                >
                  Rivoluziona la pulizia industriale con Socaf
                </Heading>
                <Flex
                  sx={{
                    alignItems: "center",
                    gap: 3,
                    bg: "light",
                    color: "primary",
                    px: 3,
                    py: 2,
                    whiteSpace: "nowrap",
                    borderRadius: "0",
                  }}
                >
                  <Text sx={{ fontSize: [0, 1, 1, 2] }}>Leggi la news</Text>
                  <ArrowRightCircle size={20} />
                </Flex>
              </Flex>
            </Link>
          )
        )}

        {/* Duplicate content to make seamless */}
        {[...Array(5)].map((_, i) =>
          helloBar.news.model.apiKey === "article" ? (
            <InboundLink
              key={`duplicate-${i}`}
              to={getArticlePath(helloBar.news, helloBar.news.locale)}
              sx={{
                display: "inline-flex",
                textDecoration: "none",
              }}
            >
              <Flex
                sx={{
                  alignItems: "center",
                  gap: 5,
                  mx: 5,
                  px: 4,
                  color: "light",
                }}
              >
                <Heading
                  as="h3"
                  variant="h4"
                  sx={{
                    my: [0],
                    fontSize: [1, 5],
                    color: "light",
                    whiteSpace: "nowrap",
                    fontWeight: "initial",
                  }}
                >
                  {helloBar.title}
                </Heading>
                <Flex
                  sx={{
                    alignItems: "center",
                    gap: 3,
                    bg: "light",
                    color: "primary",
                    px: 3,
                    py: 2,
                    whiteSpace: "nowrap",
                    borderRadius: "0",
                  }}
                >
                  <Text sx={{ fontSize: [0, 1, 1, 2] }}>Leggi la news</Text>
                  <ArrowRightCircle size={20} />
                </Flex>
              </Flex>
            </InboundLink>
          ) : (
            <Link
              key={`duplicate-${i}`}
              to={helloBar.news.url}
              target="_blank"
              sx={{
                display: "inline-flex",
                textDecoration: "none",
              }}
            >
              <Flex
                sx={{
                  alignItems: "center",
                  gap: 5,
                  mx: 5,
                  px: 4,
                  color: "light",
                }}
              >
                <Heading
                  as="h3"
                  variant="h4"
                  sx={{
                    my: [0],
                    fontSize: [1, 5],
                    color: "light",
                    whiteSpace: "nowrap",
                    fontWeight: "initial",
                  }}
                >
                  "Rivoluziona la pulizia industriale con Socaf"
                </Heading>
                <Flex
                  sx={{
                    alignItems: "center",
                    gap: 3,
                    bg: "light",
                    color: "primary",
                    px: 3,
                    py: 2,
                    whiteSpace: "nowrap",
                    borderRadius: "0",
                  }}
                >
                  <Text sx={{ fontSize: [0, 1, 1, 2] }}>Leggi la news</Text>
                  <ArrowRightCircle size={20} />
                </Flex>
              </Flex>
            </Link>
          )
        )}
      </Flex>
    </Box>
  )
}

export default BannerTop
